// Live's Open in the editor (#668, spec docs/specs/web/S1c-library-move-bulk.md): a library video
// opens its own project, as live keeps one space per file. This page reads the file, then asks
// for the brand's project made from it, or a new one: named after the file, at the frame nearest
// the video's, with the video as its first clip. It then replaces itself with that project. The
// ask is a query: it is idempotent (find or create), so reading it again is safe. The video's
// length and size are the server's, else its file's, as live's editor reads them, else live's
// stand-in (#1052, spec docs/specs/editor/open-video-length.md): a video is never left out.
// A save answers while its worker still copies the file (`pending`), and the server opens only a
// ready one, so a file still copying is read again every 2 s, for about a minute, as the library's
// saving card reads it, and opened once it is ready (spec docs/specs/editor/open-from-studio.md).
import { Anchor, Loader, Stack, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useParams } from "@tanstack/react-router";

import { useGetAsset } from "../../api/generated/endpoints/assets/assets";
import { openEditProjectFromAsset } from "../../api/generated/endpoints/editor/editor";
import type { Asset, EditAspect } from "../../api/generated/model";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { savingPollInterval, useSavingReads } from "../../platform/media/saving-poll";
import { useSourceFacts, type FileFacts } from "./length";
import { sourceKey } from "./media";
import { ASPECT_RATIOS, PROJECT_NAME_MAX } from "./project";
import { NEW_TIMELINE, editTimeline, newId } from "./timeline";
import { toWireTimeline } from "./wire";
import { readPref } from "../../platform/prefs";
import { libraryIn } from "./exits";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/editor/asset/$assetId";

/** Live's length for a video whose own cannot be read (its editor's stand-in, 4 s). */
export const UNKNOWN_LENGTH_MS = 4000;

/** The project frame nearest the video's shape; 16:9 when the file does not say. */
export function nearestAspect(width: number | null, height: number | null): EditAspect {
  if (width === null || height === null || width <= 0 || height <= 0) return "16:9";
  const shape = width / height;
  const ratio = (aspect: EditAspect) => {
    const [across, down] = aspect.split(":").map(Number);
    return across! / down!;
  };
  return ASPECT_RATIOS.reduce((best, each) =>
    Math.abs(Math.log(ratio(each) / shape)) < Math.abs(Math.log(ratio(best) / shape)) ? each : best,
  );
}

/**
 * The new project's body: the file's name, the frame nearest the video's, the video as the first
 * clip, `video` its length and size.
 */
export function projectFor(asset: Asset, video: FileFacts) {
  const timeline = editTimeline(NEW_TIMELINE, {
    type: "addClip",
    id: newId(),
    kind: "video",
    source: { kind: "asset", assetId: asset.id },
    sourceMs: video.ms,
    newTrackId: newId(),
  });
  return {
    asset_id: asset.id,
    name: asset.name.slice(0, PROJECT_NAME_MAX),
    aspect: nearestAspect(video.width, video.height),
    timeline: toWireTimeline(timeline),
  };
}

export function OpenAssetScreen() {
  const { teamId, brandId, assetId } = useParams({ from: ROUTE });
  const file = useGetAsset(teamId, brandId, assetId, {
    query: {
      refetchInterval: (query) =>
        savingPollInterval(query.state.data?.status, query.state.dataUpdateCount),
    },
  });
  const reads = useSavingReads(file, teamId, brandId, assetId);
  // Only a copied file is read and opened: a pending one has nothing at its URL yet.
  const ready = file.data?.status === "ready" ? file.data : undefined;
  const facts = useSourceFacts(sourceKey({ kind: "asset", assetId }), ready?.urls.original ?? null, {
    ms: ready?.duration_ms ?? null,
    width: ready?.width ?? null,
    height: ready?.height ?? null,
  });
  // Read, or past reading: the file gave no length, so live's stand-in, at the server's size.
  const video: FileFacts | undefined =
    facts.state === "known"
      ? facts
      : facts.state === "unknown"
        ? { ms: UNKNOWN_LENGTH_MS, width: ready?.width ?? null, height: ready?.height ?? null }
        : undefined;
  const opened = useQuery({
    queryKey: ["edit-project-from-asset", teamId, brandId, assetId],
    enabled: ready !== undefined && video !== undefined,
    queryFn: () => openEditProjectFromAsset(teamId, brandId, projectFor(ready!, video!)),
    staleTime: Infinity,
    retry: false,
  });
  if (opened.data !== undefined) {
    return (
      <Navigate
        to="/teams/$teamId/brands/$brandId/editor/$projectId"
        params={{ teamId, brandId, projectId: opened.data.id }}
        replace
      />
    );
  }
  const name = file.data?.name ?? "the video";
  const status = file.data?.status;
  const copying = status === "pending" && savingPollInterval(status, reads) !== false;
  // The copy failed, or a minute of reads found it still copying.
  const unsaved =
    status === "failed"
      ? `${name} could not be saved. Try again from its job.`
      : status === "pending" && !copying
        ? `Still saving ${name}. Reload this page in a moment to open it.`
        : null;
  const error = file.error ?? opened.error;
  if (error !== null || unsaved !== null) {
    return (
      <Stack gap="sm" p="md">
        {error !== null ? (
          <ErrorAlert error={error} />
        ) : (
          <Text size="sm" role="alert">
            {unsaved}
          </Text>
        )}
        <Anchor
          renderRoot={(props) => (
            <Link
              {...props}
              to={libraryIn(readPref("ui"))}
              params={{ teamId, brandId }}
            />
          )}
        >
          Back to the library
        </Anchor>
      </Stack>
    );
  }
  return (
    <Stack align="center" gap="sm" p="xl">
      <Loader size="sm" aria-label="Opening the video in the editor" />
      <Text size="sm">Opening the video in the editor</Text>
      {copying ? <Text size="sm">Saving {name} to the library first…</Text> : null}
    </Stack>
  );
}
